import { useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { AuthUserContext } from '../../Session';
import { FirebaseContext } from '../../Firebase';
import { useProjectSelection } from '../../ProjectSelection';
import * as ROLES from '../../../constants/roles';
import vcontrolAPI from '../../../services/documentApi';
import inventoryApi from '../../../services/inventoryApi';

import { METRIC_SCALES, IMPERIAL_SCALES } from '../../Replan/constants/scales';

const ALL_SCALES = [...METRIC_SCALES, ...IMPERIAL_SCALES];
const getScaleByKey = (key) => ALL_SCALES.find(s => s.key === key) || ALL_SCALES[0];

const collectAssemblyFromPath = async (firebase, orgValue, projectName) => {
  try {
    const asmSnapshot = await firebase.doGetProjectAssemblies(orgValue, projectName);
    if (asmSnapshot.empty) return [];

    const { getDisplayMeasurement, getDisplayAreaMeasurement } = await import('../../Replan/utils/drawingUtils');
    const results = [];

    asmSnapshot.forEach(doc => {
      const data = doc.data();
      const name = data.name || doc.id;
      const item = { name, shapeCount: data.shapeCount || 0, displayValue: '—', unit: '', pageMeasurements: data.pageMeasurements || {}, params: data.params };

      if (data.totalDisplayValue) {
        item.displayValue = data.totalDisplayValue;
        item.unit = data.totalDisplayUnit || '';
      } else if (data.totalMeasurement > 0) {
        try {
          const scaleProps = data.scaleProps || ALL_SCALES[0];
          const dpi = data.dpi || 125;
          const imageSize = data.imageSize || { width: 1000, height: 1000 };
          if (data.isArea) {
            const areaDisplay = getDisplayAreaMeasurement(data.totalMeasurement, scaleProps, dpi);
            item.displayValue = areaDisplay.value;
            item.unit = areaDisplay.unit;
          } else {
            const linearDisplay = getDisplayMeasurement(data.totalMeasurement, scaleProps, dpi, imageSize);
            item.displayValue = linearDisplay.value;
            item.unit = linearDisplay.unit;
          }
        } catch {
          item.displayValue = data.totalMeasurement.toFixed(1);
        }
      }

      results.push(item);
    });

    return results;
  } catch {
    return [];
  }
};

const aggregateAssemblyMeasurements = async (firebase, org, projectName) => {
  const [orgResults, fallbackResults] = await Promise.all([
    collectAssemblyFromPath(firebase, org, projectName),
    collectAssemblyFromPath(firebase, null, projectName),
  ]);

  const merged = new Map();
  for (const asm of [...orgResults, ...fallbackResults]) {
    if (!merged.has(asm.name)) {
      merged.set(asm.name, asm);
    }
  }

  return Array.from(merged.values())
    .sort((a, b) => b.shapeCount - a.shapeCount)
    .slice(0, 20);
};

const useHomeData = () => {
  const firebase = useContext(FirebaseContext);
  const authUser = useContext(AuthUserContext);

  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const { selectedProject: navSelectedProject, setSelectedProject: setNavSelectedProject } = useProjectSelection();
  const [selectedProject, setSelectedProject] = useState(() => {
    const storedProj = localStorage.getItem('app/selectedProject');
    const storedOrg = localStorage.getItem('app/selectedOrganization');
    return (storedProj && storedOrg) ? storedProj : null;
  });

  useEffect(() => {
    if (navSelectedProject && navSelectedProject !== selectedProject) {
      setSelectedProject(navSelectedProject);
    }
  }, [navSelectedProject]);
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);
  const [projectActivityLogs, setProjectActivityLogs] = useState([]);
  const [activitySearch, setActivitySearch] = useState('');
  const [activityFilter, setActivityFilter] = useState('all');
  const [allUsers, setAllUsers] = useState([]);
  const [serverHealth, setServerHealth] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [shapeCountsByOrg, setShapeCountsByOrg] = useState({});
  const [activeTab, setActiveTab] = useState('overview');

  const [assemblyData, setAssemblyData] = useState([]);
  const [assemblyLoading, setAssemblyLoading] = useState(false);
  const [annotatedPages, setAnnotatedPages] = useState({ total: 0, annotated: 0, thumbnails: [] });
  const [annotatedLoading, setAnnotatedLoading] = useState(false);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [poLoading, setPoLoading] = useState(false);

  const organizations = useMemo(() => {
    if (!authUser?.roles) return [];
    return Object.keys(authUser.roles).filter(k => authUser.roles[k] !== null);
  }, [authUser]);

  const [currentOrg, setCurrentOrg] = useState(() => {
    const stored = localStorage.getItem('app/selectedOrganization');
    return stored || '';
  });

  useEffect(() => {
    if (organizations.length > 0 && (!currentOrg || !organizations.includes(currentOrg))) {
      setCurrentOrg(organizations[0]);
    }
  }, [organizations, currentOrg]);

  const isOrgAdmin = useMemo(() => {
    return authUser?.roles?.[currentOrg] === ROLES.ADMIN;
  }, [authUser, currentOrg]);

  const switchOrg = useCallback((org) => {
    setCurrentOrg(org);
    localStorage.setItem('app/selectedOrganization', org);
    setSelectedProject(null);
    localStorage.removeItem('app/selectedProject');
    setActiveTab('overview');
  }, []);

  useEffect(() => {
    if (!firebase || !currentOrg) { setProjects([]); return; }
    let cancelled = false;
    setProjectsLoading(true);
    firebase.doGetOrganizationProjects(currentOrg)
      .then(orgProjects => {
        if (!cancelled) setProjects((orgProjects || []).map(p => ({ ...p, organization: currentOrg })));
      })
      .catch(() => { if (!cancelled) setProjects([]); })
      .finally(() => { if (!cancelled) setProjectsLoading(false); });
    return () => { cancelled = true; };
  }, [firebase, currentOrg]);

  useEffect(() => {
    if (!firebase || !selectedProject || !currentOrg) { setTasks([]); return; }
    let cancelled = false;
    setTasksLoading(true);
    firebase.doGetPlanningTasks(currentOrg, selectedProject)
      .then(data => { if (!cancelled) setTasks(data?.tasks ?? []); })
      .catch(() => { if (!cancelled) setTasks([]); })
      .finally(() => { if (!cancelled) setTasksLoading(false); });
    return () => { cancelled = true; };
  }, [firebase, selectedProject, currentOrg]);

  useEffect(() => {
    if (!firebase || !currentOrg) { setActivityLogs([]); return; }
    firebase.onOrgActivityFeed(currentOrg, (logs) => {
      setActivityLogs(logs.map(l => ({ ...l, organization: currentOrg })));
    }, 100);
    return () => { firebase.offOrgActivityFeed && firebase.offOrgActivityFeed(currentOrg); };
  }, [firebase, currentOrg]);

  useEffect(() => {
    if (!firebase || !currentOrg || !selectedProject) {
      setProjectActivityLogs([]);
      return;
    }
    const sub = firebase.onProjectActivityFeed(currentOrg, selectedProject, (logs) => {
      setProjectActivityLogs(logs.map(l => ({ ...l, organization: currentOrg })));
    }, 50);
    return () => { firebase.offProjectActivityFeed && firebase.offProjectActivityFeed(sub); };
  }, [firebase, currentOrg, selectedProject]);

  useEffect(() => {
    if (!firebase || !isOrgAdmin) { setAllUsers([]); return; }
    let cancelled = false;
    const onUsers = snapshot => {
      if (cancelled) return;
      const obj = snapshot.val();
      if (!obj) { setAllUsers([]); return; }
      const list = Object.keys(obj).map(k => ({ ...obj[k], uid: k }));
      setAllUsers(list);
    };
    // One-shot snapshot instead of a realtime listener: the listener re-downloads
    // the ENTIRE users collection on every write anywhere under users/ (including
    // every Replan draft save), burning RTDB download quota. Display-only data.
    firebase.users().once('value', onUsers);
    return () => { cancelled = true; };
  }, [firebase, isOrgAdmin]);

  const orgUsers = useMemo(() => {
    return allUsers.filter(u => u.roles && u.roles[currentOrg] !== null && u.roles[currentOrg] !== undefined);
  }, [allUsers, currentOrg]);

  useEffect(() => {
    if (!isOrgAdmin) return;
    vcontrolAPI.checkServerHealth()
      .then(setServerHealth)
      .catch(() => setServerHealth({ healthy: false, status: 'unreachable', responseTime: 0 }));
  }, [isOrgAdmin]);

  useEffect(() => {
    if (!isOrgAdmin || !currentOrg) { setDocuments([]); return; }
    vcontrolAPI.getOrganizationDocuments([currentOrg])
      .then(docs => setDocuments(Array.isArray(docs) ? docs : []))
      .catch(() => setDocuments([]));
  }, [isOrgAdmin, currentOrg]);

  useEffect(() => {
    if (!firebase || !isOrgAdmin || projects.length === 0) { setShapeCountsByOrg({}); return; }
    let cancelled = false;
    const counts = { pages: 0, projects: 0 };
    Promise.all(projects.map(project =>
      firebase.doGetProjectVersions(currentOrg, project.id)
        .then(versions => {
          if (!versions || versions.length === 0) return;
          return firebase.doGetAllPagesWithShapes(currentOrg, project.id, versions);
        })
        .then(versionPagesMap => {
          if (!versionPagesMap) return;
          let total = 0;
          versionPagesMap.forEach(ps => { total += ps.size; });
          if (total > 0) { counts.pages += total; counts.projects += 1; }
        })
        .catch(() => { })
    )).then(() => {
      if (!cancelled) setShapeCountsByOrg(counts);
    });
    return () => { cancelled = true; };
  }, [firebase, isOrgAdmin, projects, currentOrg]);

  const refreshHealth = useCallback(async () => {
    try {
      const result = await vcontrolAPI.checkServerHealth();
      setServerHealth(result);
    } catch {
      setServerHealth({ healthy: false, status: 'unreachable', responseTime: 0 });
    }
  }, []);

  const taskStats = useMemo(() => {
    const total = tasks.length;
    if (!total) return { total: 0, done: 0, inProgress: 0, notStarted: 0, avgProgress: 0 };
    const done = tasks.filter(t => (t.progress ?? 0) >= 100).length;
    const inProgress = tasks.filter(t => (t.progress ?? 0) > 0 && (t.progress ?? 0) < 100).length;
    const notStarted = tasks.filter(t => (t.progress ?? 0) === 0).length;
    const avgProgress = tasks.reduce((sum, t) => sum + (t.progress ?? 0), 0) / total;
    return { total, done, inProgress, notStarted, avgProgress };
  }, [tasks]);

  const actionOptions = useMemo(() => {
    const actions = new Set(activityLogs.map(l => l.action).filter(Boolean));
    return Array.from(actions).sort();
  }, [activityLogs]);

  const selectProject = useCallback((projectName) => {
    setSelectedProject(projectName);
    setNavSelectedProject(projectName || '');
    if (projectName) {
      localStorage.setItem('app/selectedOrganization', currentOrg);
    }
  }, [currentOrg, setNavSelectedProject]);

  const selectedProjectObj = useMemo(
    () => projects.find(p => p.id === selectedProject)
      || projects.find(p => p.name === selectedProject)
      || null,
    [projects, selectedProject],
  );
  const selectedProjectDate = useMemo(() => {
    const ts = selectedProjectObj?.updatedAt || selectedProjectObj?.createdAt;
    if (!ts) return null;
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return Number.isNaN(d.getTime()) ? null : d;
  }, [selectedProjectObj]);

  // One-time migration: if a stale localStorage value (project name) is in
  // selectedProject, resolve it to the current project.id so downstream
  // Firestore queries (which use the value as the doc id) hit the right doc.
  useEffect(() => {
    if (!selectedProject || projects.length === 0) return;
    if (projects.some(p => p.id === selectedProject)) return;
    const byName = projects.find(p => p.name === selectedProject);
    if (byName && byName.id !== selectedProject) {
      setSelectedProject(byName.id);
      setNavSelectedProject(byName.id);
    }
  }, [projects, selectedProject]);

  // Assembly measurements — computed when selectedProject changes
  useEffect(() => {
    if (!firebase || !currentOrg || !selectedProject) {
      setAssemblyData([]);
      return;
    }
    let cancelled = false;
    setAssemblyLoading(true);
    aggregateAssemblyMeasurements(firebase, currentOrg, selectedProject)
      .then(result => { if (!cancelled) setAssemblyData(result); })
      .catch(() => { if (!cancelled) setAssemblyData([]); })
      .finally(() => { if (!cancelled) setAssemblyLoading(false); });
    return () => { cancelled = true; };
  }, [firebase, currentOrg, selectedProject]);

  // Annotated pages — fetched when selectedProject changes
  useEffect(() => {
    if (!firebase || !currentOrg || !selectedProject) {
      setAnnotatedPages({ total: 0, annotated: 0, thumbnails: [] });
      return;
    }
    let cancelled = false;
    setAnnotatedLoading(true);

    const fetchAnnotatedPages = async () => {
      try {
        // 1) Source of truth for pages — VControl document info endpoint
        //    (same one used by the Replan PageSelectorModal). Returns all
        //    pages across all versions of the project with their metadata.
        const projectPages = await vcontrolAPI.getDocumentInfo(selectedProject);
        if (cancelled) return;
        const totalPages = projectPages.length;

        // 2) For each version present, ask Firestore which pages have shapes
        const versions = [
          ...new Set(projectPages.map(p => p.version).filter(Boolean)),
        ];
        const pagesWithShapes = versions.length > 0
          ? await firebase.doGetAllPagesWithShapes(
              currentOrg,
              selectedProject,
              versions,
            )
          : new Map();
        if (cancelled) return;

        // 3) Count of pages with shapes (sum of Set sizes per version)
        let totalAnnotated = 0;
        pagesWithShapes.forEach((pageSet) => {
          totalAnnotated += pageSet.size;
        });

        // 4) Thumbnails: first 6 pages of the project, preferring annotated ones.
        //
        // BUG FIX (Fix 2): doGetAllPagesWithShapes returns document IDs parsed
        // as integers — these are 0-based page indices (version-relative ordinals
        // matching getPageIndexInVersion in Replan.js). The filter must compare
        // using the same 0-based ordinal, not the 1-based actualPageNumber.
        //
        // BUG FIX (Fix 8): p.pageIndex from getDocumentInfo is a global position
        // across ALL pages/versions, NOT a version-relative ordinal. We must
        // compute the version-relative ordinal from the full projectPages array
        // so it matches the Firestore key format (version|ordinal) written by
        // Replan.js:302 (pageKey = `${version}|${pageIndex}` where pageIndex
        // = getPageIndexInVersion).
        const annotatedSet = new Set();
        pagesWithShapes.forEach((pageSet, version) => {
          pageSet.forEach((pageId) => {
            annotatedSet.add(`${version}::${pageId}`);
          });
        });

        // Compute version-relative ordinals (0-based) from the full page list.
        // This matches getPageIndexInVersion's logic: the ordinal position
        // of the page within all pages sharing the same version.
        const verCounters = {};
        const verOrdinalMap = new Map(); // key: `${version}::${docId}::${actualPageNum}`
        projectPages.forEach((p) => {
          if (!p.version) return;
          const v = p.version;
          if (!(v in verCounters)) verCounters[v] = 0;
          const mapKey = `${v}::${p.documentId}::${p.actualPageNumber}`;
          if (!verOrdinalMap.has(mapKey)) {
            verOrdinalMap.set(mapKey, verCounters[v]++);
          }
        });

        const getVerOrdinal = (p) => {
          if (!p.version) return null;
          return verOrdinalMap.get(`${p.version}::${p.documentId}::${p.actualPageNumber}`) ?? null;
        };

        const annotatedFirst = projectPages.filter((p) => {
          if (!p.version) return false;
          const zeroBased = getVerOrdinal(p);
          if (zeroBased == null) {
            // Fallback for pages not found in the ordinal map
            const fb = typeof p.actualPageNumber === 'number' ? p.actualPageNumber - 1 : null;
            return fb != null && annotatedSet.has(`${p.version}::${fb}`);
          }
          return annotatedSet.has(`${p.version}::${zeroBased}`);
        });

        const thumbnails = annotatedFirst.map((p, i) => ({
          documentId: p.documentId,
          actualPageNumber: p.actualPageNumber,
          // Use the version-relative ordinal so that getCandidateKeys
          // can match the Firestore pageKey format (version|ordinal).
          pageIndex: getVerOrdinal(p)
            ?? (typeof p.actualPageNumber === 'number' ? p.actualPageNumber - 1 : i),
          version: p.version,
          folderPath: p.folderPath,
          label: p.pageLabel || `Pg ${p.actualPageNumber ?? i + 1}`,
          url: p.url,
        }));

        if (!cancelled) {
          setAnnotatedPages({
            total: totalPages,
            annotated: totalAnnotated,
            thumbnails,
          });
        }
      } catch (error) {
        console.error('Failed to fetch annotated pages:', error);
        if (!cancelled) setAnnotatedPages({ total: 0, annotated: 0, thumbnails: [] });
      } finally {
        if (!cancelled) setAnnotatedLoading(false);
      }
    };

    fetchAnnotatedPages();
    return () => { cancelled = true; };
  }, [firebase, currentOrg, selectedProject]);

  // Purchase orders — fetched when selectedProject changes
  useEffect(() => {
    if (!currentOrg || !selectedProject) {
      setPurchaseOrders([]);
      return;
    }
    let cancelled = false;
    setPoLoading(true);
    inventoryApi.getPurchaseOrders(currentOrg)
      .then(orders => {
        if (!cancelled) setPurchaseOrders(Array.isArray(orders) ? orders : []);
      })
      .catch(() => { if (!cancelled) setPurchaseOrders([]); })
      .finally(() => { if (!cancelled) setPoLoading(false); });
    return () => { cancelled = true; };
  }, [currentOrg, selectedProject]);

  return {
    authUser,
    firebase,
    projects, projectsLoading,
    selectedProject, selectProject, selectedProjectDate,
    tasks, tasksLoading, taskStats,
    activityLogs, projectActivityLogs, activitySearch, setActivitySearch,
    activityFilter, setActivityFilter, actionOptions,
    orgUsers, allUsers,
    serverHealth, documents, shapeCountsByOrg,
    refreshHealth,
    organizations, currentOrg, switchOrg,
    isOrgAdmin,
    activeTab, setActiveTab,
    assemblyData, assemblyLoading,
    annotatedPages, annotatedLoading,
    purchaseOrders, poLoading,
  };
};

export default useHomeData;
